import { useGetAllShopEmailTemplates } from "@babackapp/private-api-client/client/use-get-all-shop-email-templates";
import { Button } from "@babackapp/ui/components/button";
import { FormField } from "@babackapp/ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { useTranslation } from "react-i18next";
import { Eye, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { useWorkflowForm } from "../../context/workflows-editor-workflows-context";
import useCurrentShopSuspense from "../../hooks/use-current-shop-suspense";
import { useNodeFormPath } from "../../hooks/use-node-form-path";
import { PanelContent } from "../../lib/panel/panel-content";
import type { EmailOverrides } from "./apply-email-overrides";
import { EmailCustomizationSheet } from "./email-customization-sheet";
import { EmailPreviewSheet } from "./email-preview-sheet";

export function PanelEmailTemplate() {
  const form = useWorkflowForm();
  const { t } = useTranslation("trackoffice");
  const { data: shop } = useCurrentShopSuspense();
  const { data: allTemplates = [] } = useGetAllShopEmailTemplates(shop.id);
  const fieldPath = useNodeFormPath("config.templateId");
  const overridesPath = useNodeFormPath("config.overrides");
  const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const overrides = overridesPath
    ? (form.watch(overridesPath) as EmailOverrides | null)
    : null;

  const overrideCount = useMemo(() => {
    if (!overrides) return 0;
    return (
      (overrides.subject ? 1 : 0) +
      Object.keys(overrides.customBlocks ?? {}).length +
      (overrides.hiddenBlocks?.length ?? 0)
    );
  }, [overrides]);

  if (!fieldPath) {
    return null;
  }

  // Filter templates to only show 'email' type
  const templates = allTemplates?.filter(
    (template) => template.type === "email",
  );

  return (
    <>
      <PanelContent
        title={t("workflowsEditor.emailTemplate.title", "Template")}
      >
        <FormField
          control={form.control}
          name={fieldPath}
          render={({ field }) => {
            const selectedTemplate = templates.find(
              (template) => template.id === field.value,
            );

            return (
              <>
                <Select
                  value={field.value as string}
                  onValueChange={field.onChange}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue>
                      {selectedTemplate?.title ||
                        t(
                          "workflowsEditor.emailTemplate.default",
                          "Default",
                        )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="null">
                      <div className="flex flex-col gap-1">
                        <span className="text-xs">
                          {t(
                            "workflowsEditor.emailTemplate.default",
                            "Default",
                          )}
                        </span>
                        <span className="text-[10px] text-muted-foreground">
                          {t(
                            "workflowsEditor.emailTemplate.defaultDescription",
                            "Use default email template",
                          )}
                        </span>
                      </div>
                    </SelectItem>
                    {templates?.map((template) => (
                      <SelectItem key={template.id} value={template.id}>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs">{template.title}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {selectedTemplate && (
                  <>
                    <div className="flex flex-col gap-1.5 mt-2">
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 gap-2 text-xs"
                          onClick={() => setIsCustomizeOpen(true)}
                        >
                          <SlidersHorizontal className="h-3.5 w-3.5" />
                          {t(
                            "workflowsEditor.emailCustomization.customize",
                            "Customize",
                          )}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 gap-2 text-xs"
                          onClick={() => setIsPreviewOpen(true)}
                        >
                          <Eye className="h-3.5 w-3.5" />
                          {t(
                            "workflowsEditor.emailPreview.preview",
                            "Preview",
                          )}
                        </Button>
                      </div>
                      {overrideCount > 0 && (
                        <span className="text-[10px] text-muted-foreground">
                          {t(
                            "workflowsEditor.emailCustomization.activeOverrides",
                            "{count, plural, one {# customization active} other {# customizations active}}",
                            { count: overrideCount },
                          )}
                        </span>
                      )}
                    </div>

                    <EmailCustomizationSheet
                      isOpen={isCustomizeOpen}
                      onOpenChange={setIsCustomizeOpen}
                      template={selectedTemplate}
                    />
                    <EmailPreviewSheet
                      isOpen={isPreviewOpen}
                      onOpenChange={setIsPreviewOpen}
                      template={selectedTemplate}
                      overrides={overrides}
                    />
                  </>
                )}
              </>
            );
          }}
        />
      </PanelContent>
    </>
  );
}
